import type { ClauseMiningDryRun } from '@clm/shared-types';

import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { summariseFilter, summariseResult, truncate } from './shared';

/**
 * The last twenty terminal dry runs — CLM-103 (first slice).
 *
 * API-originated dry runs only: a break-glass CLI / `mine-clauses.yml` run
 * writes no `clause_mining_dry_run` row, and the caption says so. `failed`
 * rows carry the `bad` tone; a long `error_summary` is cut for the cell with
 * the full text in `title`.
 */

const COLUMNS: readonly DataTableColumn<ClauseMiningDryRun>[] = [
  {
    id: 'finished',
    header: 'Finished',
    cell: (r) => (r.finished_utc === null ? '—' : <time dateTime={r.finished_utc}>{r.finished_utc}</time>),
  },
  { id: 'stage', header: 'Stage', cell: (r) => r.stage },
  {
    id: 'status',
    header: 'Status',
    cell: (r) => (
      <span data-tone={r.status === 'failed' ? 'bad' : r.status === 'cancelled' ? 'warn' : 'plain'} data-status={r.status}>
        {r.status}
      </span>
    ),
  },
  { id: 'requested_by', header: 'Requested by', cell: (r) => r.requested_by_upn },
  { id: 'approved_by', header: 'Approved by', cell: (r) => r.approved_by_upn ?? '—' },
  { id: 'filter', header: 'Filter', cell: (r) => summariseFilter(r.filter) },
  { id: 'result', header: 'Result', cell: (r) => summariseResult(r) },
  {
    id: 'error',
    header: 'Error',
    cell: (r) => (r.error_summary === null ? '—' : <span title={r.error_summary}>{truncate(r.error_summary)}</span>),
  },
];

export function DryRunHistory({ runs }: { readonly runs: readonly ClauseMiningDryRun[] }) {
  return (
    <DataTable
      caption="Dry-run history — the last twenty API-originated dry runs; break-glass CLI runs leave no row"
      columns={COLUMNS}
      rows={runs}
      rowKey={(r) => r.clause_mining_dry_run_id}
      empty={<p>No dry run has finished yet.</p>}
    />
  );
}
